import qs from 'query-string';

const QueryKeyList = [
  'adsource',
  'appId',
  'from',
  'source',
  'activityId',
  'market_activity_code',
] as const;
type QueryKey = typeof QueryKeyList[number];
type ParsedQuery = qs.ParsedQuery<string>;
type NormalizedQueryValue = string | string[];
type NormalizedQuery = Record<string, NormalizedQueryValue>;

/** 标准化查询参数值 */
const normQueryVal = (value: ParsedQuery[string]): NormalizedQueryValue => {
  if (!value) {
    return '';
  }

  if (Array.isArray(value)) {
    return value.map((rawValue) => normQueryVal(rawValue) as string);
  }

  // 处理解析出的 'MALL_ACT_23930/#/order' 这种格式的参数值
  const match = value.match(/[^#/]*/);
  return match ? match[0] : '';
};

/**
 * 根据qs解析得到的查询字符串映射表得到指定key的值
 *
 * ! 如果存在多个值，默认取最后一个
*/
export function getLastQueryVal(rawValue: NormalizedQueryValue) {
  let value: string;

  if (Array.isArray(rawValue)) {
    // 如果误传入了多个值，那么取最后一个
    value = rawValue[rawValue.length - 1];
  } else {
    value = rawValue;
  }

  return value;
}

/**
 * 处理微信环境下查询字符串出现在路由#号之前的问题
 * @param getLastValue 是否使用最后出现的值（如果链接上存在多个同名key）
 * @returns
 */
export function parseQueryStr<T>(getLastValue?: T) {
  type ParsedResult = T extends readonly string[]
    // 数组项类型是否不为字符串字面量
    ? string extends T[number]
      ? NormalizedQuery
      : NormalizedQuery & Record<T[number], string>
    : T extends true
      ? Record<string, string>
      : NormalizedQuery;
  const searches = window.location.href.split('?').slice(1);
  /** 是否使用白名单——出现在白名单中的key对应的value会被取最后一次出现的值 */
  const useWhiteList = Array.isArray(getLastValue);
  const getValue = (key: string, val: NormalizedQueryValue): string | NormalizedQueryValue => {
    if (
      (useWhiteList && getLastValue.includes(key))
      || getLastValue
    ) {
      return getLastQueryVal(val);
    }

    return val;
  };
  return searches.reduce((acc, search) => ({
    ...acc,
    ...Object.entries(qs.parse(search))
      .map<[string, NormalizedQueryValue]>(([key, value]) => [key, normQueryVal(value)])
      .reduce((normalizedAcc, [key, normalizedValue]) => ({
        ...normalizedAcc,
        [key]: getValue(key, normalizedValue),
      }), {}),
  }), {} as ParsedResult);
}

const AppQueryConfigKeyList = [
  'adsource',
  'algorithmPriceFlag',
  'applicationScene',
  'appId',
  'activityId',
  'market_activity_code',
  'scene_code',
  'sub_scene_code',
] as const;
type AppQueryConfigKey = typeof AppQueryConfigKeyList[number];

// const getCacheKey = (rawKey: string) => `AppBikeMallH5_query_${rawKey}`
const getCacheValue = (query: Partial<Record<QueryKey, string>>, key: AppQueryConfigKey) => {
  if (key === 'adsource') {
    const {
      adsource,
      from,
      source,
    } = query;
    return adsource || from || source || '';
  }

  return (query as Record<string, string>)[key] || '';
};

/** 应用打开时根据解析查询字符串获得的配置 */
export const appQueryConfig: Record<AppQueryConfigKey, string> = Object.create(null);
// * 初始化
AppQueryConfigKeyList.forEach((configKey) => { appQueryConfig[configKey] = ''; });

/** 根据url上的参数来初始化一些应用配置 */
function initAppQueryConfig() {
  const query = parseQueryStr();
  const resolvedQuery = QueryKeyList.reduce((acc, key) => {
    const value = getLastQueryVal(query[key]);

    if (!value) {
      return acc;
    }

    return {
      ...acc,
      [key]: value,
    };
  }, {} as Partial<Record<QueryKey, string>>);
  AppQueryConfigKeyList.forEach((key) => {
    appQueryConfig[key] = getCacheValue(resolvedQuery, key);
  });
}

/** 加载浏览任务组件 */
function initVisitTask() {
  const query = parseQueryStr();
  const isLoadTask = !!getLastQueryVal(query.fromTaskVisit);

  if (isLoadTask) {
    const script = document.createElement('script');
    script.src = 'https://m.hellobike.com/npm/@hb/visit-task-component@1.1.7/dist/visitTask.min.js';
    document.body.appendChild(script);
  }
}

/**
 * 极简版URL参数解析（适配hash中的参数）
 * @param {string} [url] - 可选，指定URL，默认用当前页面URL
 * @returns {Object} 参数对象
 */
export function getUrlParams(url = window.location.href) {
  // 提取所有?后的参数部分（包括hash里的）
  const paramStr = url.split('?')[1] || '';
  // 按&分割并转为对象，自动处理空值
  return paramStr.split('&').reduce((params, item) => {
    const [key, value] = item.split('=');
    params[key] = value ? decodeURIComponent(value) : '';
    return params;
  }, {});
}

// * 需要在应用初始化之前被调用
initAppQueryConfig();
initVisitTask();
